import { useParams, useNavigate } from "react-router-dom";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { motion } from "framer-motion";
import { useState, useEffect, useCallback } from "react";
import {
  ArrowLeft,
  Download,
  RefreshCw,
  Clock,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  XCircle,
  User,
  FileText,
  Image as ImageIcon,
  Video,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { VideoPlayer } from "@/components/dashboard/VideoPlayer";
import { GenerationTimeline } from "@/components/dashboard/GenerationTimeline";
import { PersonaReveal } from "@/components/dashboard/PersonaReveal";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { handleEdgeFunctionError, formatErrorForToast, translateTaskErrorMessage } from "@/lib/errorHandler";

interface Task {
  id: string;
  user_id: string;
  status: string;
  product_name: string;
  product_description: string | null;
  input_image_url: string | null;
  prompt_persona: Record<string, string> | null;
  prompts: { raw_scripts?: string } | null;
  prompt_array: Array<{ index: number; script: string }> | null;
  flux_images: Array<{ url: string; frame: number }> | null;
  video_assets: Array<{ id: number; url: string; thumbnail: string; duration: number }> | null;
  error_message: string | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
  current_step: string | null;
  image_generated_url: string | null;
  kie_video_tasks: Array<{ index: number; status: string; video_url: string | null; kie_task_id: string | null; error_message?: string | null }> | null;
}

const statusConfig: Record<
  string,
  { label: string; color: string; icon: React.ComponentType<{ className?: string }>; progress: number }
> = {
  pending: { label: "Pendente", color: "text-blue-400", icon: Clock, progress: 10 },
  moderating: { label: "Validando imagem", color: "text-amber-400", icon: Loader2, progress: 5 },
  processing: { label: "Processando", color: "text-amber-400", icon: Loader2, progress: 30 },
  generating_image: { label: "Gerando imagem", color: "text-amber-400", icon: Loader2, progress: 50 },
  generating_video: { label: "Gerando vídeos", color: "text-amber-400", icon: Loader2, progress: 75 },
  completed: { label: "Finalizada", color: "text-primary", icon: CheckCircle2, progress: 100 },
  finished: { label: "Finalizada", color: "text-primary", icon: CheckCircle2, progress: 100 },
  expired: { label: "Expirada", color: "text-muted-foreground", icon: AlertTriangle, progress: 100 },
  partial_success: { label: "Parcialmente Concluída", color: "text-amber-400", icon: AlertTriangle, progress: 100 },
  error: { label: "Erro", color: "text-destructive", icon: XCircle, progress: 0 },
};

const getStatusConfig = (status: string) => {
  return statusConfig[status] || { label: status, color: "text-muted-foreground", icon: Clock, progress: 0 };
};

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function TaskDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  const fetchTask = async (showLoading = true) => {
    if (!id) return;

    if (showLoading) setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/login");
        return;
      }

      const response = await supabase.functions.invoke(`tasks?id=${id}`, {
        method: "GET",
      });

      if (response.error || response.data?.error) {
        const errorInfo = handleEdgeFunctionError(response.error, response.data, 'task');
        if (showLoading) {
          toast(formatErrorForToast(errorInfo));
        }
        return;
      }

      setTask(response.data.task);
    } catch (error) {
      console.error("Error fetching task:", error);
      if (showLoading) {
        const errorInfo = handleEdgeFunctionError(error, null, 'task');
        toast(formatErrorForToast(errorInfo));
      }
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchTask();
  }, [id, navigate]);

  // Auto-refresh when task is processing
  useEffect(() => {
    if (!task) return;

    const isProcessing = ["pending", "moderating", "processing", "generating_image", "generating_video"].includes(task.status);

    if (isProcessing) {
      const interval = setInterval(() => {
        fetchTask(false);
      }, 10000); // Refresh every 10 seconds

      return () => clearInterval(interval);
    }
  }, [task?.status, id]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 w-10" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-4 w-96" />
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Skeleton className="h-80" />
            <Skeleton className="h-80" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!task) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <h2 className="text-2xl font-bold mb-4">Task não encontrada</h2>
          <Button onClick={() => navigate("/dashboard")}>Voltar ao Dashboard</Button>
        </div>
      </DashboardLayout>
    );
  }

  const status = getStatusConfig(task.status);
  const StatusIcon = status.icon;
  const isProcessing = ["pending", "moderating", "processing", "generating_image", "generating_video"].includes(task.status);

  const handleReprocess = async () => {
    setIsReprocessing(true);
    try {
      const response = await supabase.functions.invoke(`tasks?id=${id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: { id },
      });

      if (response.error || response.data?.error) {
        const errorInfo = handleEdgeFunctionError(response.error, response.data, 'task');
        toast(formatErrorForToast(errorInfo));
        return;
      }

      const stepLabels: Record<string, string> = {
        persona: "geração de persona",
        image: "geração de imagem",
        video: "geração de vídeos",
      };

      const fromStep = response.data?.from_step || "persona";
      const stepLabel = stepLabels[fromStep] || fromStep;

      toast({
        title: "Reprocessamento iniciado",
        description: `Continuando da etapa: ${stepLabel}`,
      });

      // Update task state immediately to trigger polling
      await fetchTask();
    } catch (error) {
      console.error("Error reprocessing task:", error);
      const errorInfo = handleEdgeFunctionError(error, null, 'task');
      toast(formatErrorForToast(errorInfo));
    } finally {
      setIsReprocessing(false);
    }
  };

  const handleDownloadAll = async () => {
    if (isDownloading) return;

    setIsDownloading(true);
    toast({
      title: "Preparando download",
      description: "Criando arquivo ZIP com todos os vídeos...",
    });

    try {
      const zip = new JSZip();
      const videosFolder = zip.folder("videos");

      for (let i = 0; i < allVideos.length; i++) {
        const video = allVideos[i];
        if (video.url) {
          try {
            const response = await fetch(video.url);
            const blob = await response.blob();
            const sanitizedName = task.product_name.replace(/[^a-zA-Z0-9]/g, '_');
            videosFolder?.file(`${sanitizedName}_video_${i + 1}.mp4`, blob);
          } catch (error) {
            console.error(`Error fetching video ${i + 1}:`, error);
          }
        }
      }

      // Add image if available
      if (task.image_generated_url) {
        try {
          const imgResponse = await fetch(task.image_generated_url);
          const imgBlob = await imgResponse.blob();
          const sanitizedName = task.product_name.replace(/[^a-zA-Z0-9]/g, '_');
          zip.file(`${sanitizedName}_imagem_9x16.png`, imgBlob);
        } catch (error) {
          console.error("Error fetching image:", error);
        }
      }

      const zipBlob = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 }
      });

      const sanitizedName = task.product_name.replace(/[^a-zA-Z0-9]/g, '_');
      saveAs(zipBlob, `${sanitizedName}_ugc_videos.zip`);

      toast({
        title: "Download concluído",
        description: `${allVideos.length} vídeos baixados em ZIP.`,
      });
    } catch (error) {
      console.error("Error creating ZIP:", error);
      toast({
        variant: "destructive",
        title: "Erro no download",
        description: "Não foi possível criar o arquivo ZIP.",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const thumbnail = task.image_generated_url || task.input_image_url || "https://placehold.co/400x400/1a1a1a/14b8a6?text=Sem+Imagem";

  // Get completed videos from kie_video_tasks
  const completedVideos = task.kie_video_tasks?.filter(v => v.video_url) || [];
  const failedVideos = task.kie_video_tasks?.filter(v => v.status === 'failed') || [];
  const videoAssets = task.video_assets || [];
  const allVideos = completedVideos.length > 0
    ? completedVideos.map((v, i) => ({ id: v.index, url: v.video_url!, thumbnail: thumbnail }))
    : videoAssets;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{task.product_name}</h1>
            <p className="text-muted-foreground">{task.product_description}</p>
          </div>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${status.color} bg-secondary`}>
            <StatusIcon className={`w-4 h-4 ${isProcessing ? "animate-spin" : ""}`} />
            <span className="font-medium">{status.label}</span>
          </div>
        </div>

        {/* Error Message */}
        {task.error_message && (() => {
          const translated = translateTaskErrorMessage(task.error_message);
          return (
            <Card className="border-destructive">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-destructive mb-1">Erro na geração</p>
                    <p className="text-sm text-muted-foreground">{translated.message}</p>

                    {translated.type === 'quota' && (
                      <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                        <p className="text-sm text-amber-400 font-medium mb-1">⚠️ Limite do Sistema</p>
                        <p className="text-sm text-muted-foreground">
                          Este é um problema temporário do sistema. Por favor, entre em contato com o suporte
                          ou tente novamente mais tarde.
                        </p>
                      </div>
                    )}

                    {translated.type === 'photorealistic' && (
                      <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                        <p className="text-sm text-amber-400 font-medium mb-1">⚠️ Aviso importante</p>
                        <p className="text-sm text-muted-foreground">
                          Por favor, utilize uma imagem do produto sem pessoas ou com ilustrações/desenhos.
                        </p>
                      </div>
                    )}

                    {translated.type === 'moderation' && (
                      <div className="mt-3 p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
                        <p className="text-sm text-destructive font-medium mb-1">🚫 Conteúdo não permitido</p>
                        <p className="text-sm text-muted-foreground">
                          Por favor, envie uma imagem diferente do produto que não contenha conteúdo sensível.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Partial Success / Failed Videos Banner */}
        {(task.status === 'partial_success' || (task.status === 'completed' && failedVideos.length > 0)) && (
          <Card className="border-amber-500/50 bg-amber-500/5">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-medium text-amber-500 mb-1">
                    ⚠️ Geração parcial: {completedVideos.length}/3 vídeos disponíveis
                  </p>
                  <p className="text-sm text-muted-foreground mb-3">
                    {failedVideos.length === 1
                      ? `1 vídeo não pôde ser gerado.`
                      : `${failedVideos.length} vídeos não puderam ser gerados.`
                    } Você pode baixar os vídeos disponíveis ou tentar novamente.
                  </p>
                  {failedVideos.map(v => (
                    <div key={v.index} className="mt-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-xs">
                      <span className="font-medium">Vídeo {v.index}:</span> {v.error_message || 'Erro desconhecido'}
                    </div>
                  ))}
                  <div className="flex gap-2 mt-4">
                    {completedVideos.length > 0 && (
                      <Button size="sm" onClick={handleDownloadAll} disabled={isDownloading}>
                        {isDownloading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        Baixar disponíveis ({completedVideos.length})
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={handleReprocess} disabled={isReprocessing}>
                      {isReprocessing ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Tentar novamente
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Generation Timeline */}
        {isProcessing && (
          <Card>
            <CardContent className="p-5">
              <GenerationTimeline currentStatus={task.status} />
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Product Image */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <ImageIcon className="w-5 h-5" />
                {task.image_generated_url ? "Imagem Gerada (9:16)" : "Imagem do Produto"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <img
                src={thumbnail}
                alt={task.product_name}
                className="w-full max-h-96 object-contain rounded-lg bg-secondary"
              />
            </CardContent>
          </Card>

          {/* Persona */}
          <PersonaReveal
            persona={task.prompt_persona}
            isLoading={isProcessing && !task.prompt_persona}
          />

          {/* Scripts - Full width */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="w-5 h-5" />
                Roteiros ({task.prompt_array?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {task.prompt_array && task.prompt_array.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {task.prompt_array.map((script) => (
                    <div key={script.index} className="p-4 bg-secondary/50 rounded-lg">
                      <p className="text-xs font-medium text-primary mb-2">Roteiro {script.index}</p>
                      <p className="text-sm text-muted-foreground">{script.script}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-32 flex items-center justify-center bg-secondary/50 rounded-lg">
                  <p className="text-muted-foreground">
                    {isProcessing ? "Gerando roteiros..." : "Aguardando geração..."}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Videos */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Video className="w-5 h-5" />
              Vídeos Gerados ({allVideos.length}/3)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Show all 3 video slots with individual status */}
            {(task.status === 'partial_success' || task.status === 'completed' || task.status === 'finished' || task.kie_video_tasks?.length === 3) ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map((videoIndex) => {
                  const videoTask = task.kie_video_tasks?.find(v => v.index === videoIndex);
                  const completedVideo = allVideos.find(v => v.id === videoIndex);
                  const isCompleted = videoTask?.status === 'completed' && completedVideo?.url;
                  const isFailed = videoTask?.status === 'failed';

                  return (
                    <motion.div
                      key={videoIndex}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: videoIndex * 0.1 }}
                      className={isFailed ? 'opacity-60' : ''}
                    >
                      {isCompleted && completedVideo?.url ? (
                        <VideoPlayer
                          url={completedVideo.url}
                          thumbnail={thumbnail}
                          title={`${task.product_name}_video_${videoIndex}`}
                          onDownload={() => toast({
                            title: "Download iniciado",
                            description: `Baixando vídeo ${videoIndex}...`,
                          })}
                        />
                      ) : isFailed ? (
                        <div className="aspect-[9/16] bg-destructive/10 border border-destructive/30 rounded-lg flex flex-col items-center justify-center p-4">
                          <XCircle className="w-10 h-10 text-destructive mb-3" />
                          <p className="text-sm font-medium text-destructive mb-1">Falhou</p>
                          <p className="text-xs text-center text-muted-foreground line-clamp-3">
                            {videoTask?.error_message || 'Erro desconhecido'}
                          </p>
                        </div>
                      ) : (
                        <div className="aspect-[9/16] bg-secondary/50 rounded-lg flex flex-col items-center justify-center">
                          <Loader2 className="w-8 h-8 text-muted-foreground animate-spin mb-2" />
                          <p className="text-sm text-muted-foreground">Processando...</p>
                        </div>
                      )}
                      <p className={`text-sm text-center mt-2 ${isFailed ? 'text-destructive' : isCompleted ? 'text-primary' : 'text-muted-foreground'}`}>
                        Vídeo {videoIndex} {isCompleted ? '✓' : isFailed ? '✗' : ''}
                      </p>
                    </motion.div>
                  );
                })}
              </div>
            ) : allVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {allVideos.map((video, index) => (
                  <motion.div
                    key={video.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {video.url ? (
                      <VideoPlayer
                        url={video.url}
                        thumbnail={thumbnail}
                        title={`${task.product_name}_video_${index + 1}`}
                        onDownload={() => toast({
                          title: "Download iniciado",
                          description: `Baixando vídeo ${index + 1}...`,
                        })}
                      />
                    ) : (
                      <div className="aspect-[9/16] bg-secondary rounded-lg overflow-hidden">
                        <img
                          src={video.thumbnail}
                          alt={`Vídeo ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <p className="text-sm text-center mt-2 text-muted-foreground">
                      Vídeo {index + 1}
                    </p>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="h-48 flex flex-col items-center justify-center bg-secondary/50 rounded-lg">
                {isProcessing ? (
                  <>
                    <Loader2 className="w-8 h-8 text-muted-foreground animate-spin mb-4" />
                    <p className="text-muted-foreground">Gerando vídeos...</p>
                  </>
                ) : task.status === "error" ? (
                  <p className="text-muted-foreground">Não foi possível gerar os vídeos</p>
                ) : (
                  <p className="text-muted-foreground">Aguardando geração...</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center gap-4">
          {(task.status === "completed" || task.status === "finished" || task.status === "partial_success") && allVideos.length > 0 && (
            <Button
              variant="default"
              className="gap-2"
              onClick={handleDownloadAll}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              {isDownloading ? "Preparando ZIP..." : `Baixar ZIP (${allVideos.length} vídeos)`}
            </Button>
          )}
          {(task.status === "error" || task.status === "expired" || task.status === "partial_success") && (
            <Button
              variant="outline"
              className="gap-2"
              onClick={handleReprocess}
              disabled={isReprocessing}
            >
              {isReprocessing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Reprocessar
            </Button>
          )}
        </div>

        {/* Meta Info */}
        <div className="flex items-center gap-6 text-sm text-muted-foreground flex-wrap">
          <span>Criada em: {formatDate(task.created_at)}</span>
          {task.expires_at && (
            <>
              <span>•</span>
              <span>Expira em: {formatDate(task.expires_at)}</span>
            </>
          )}
          <span>•</span>
          <span>ID: {task.id}</span>
        </div>
      </div>
    </DashboardLayout>
  );
}
